import {
  API_MAX_PAGE_SIZE,
  API_MIN_PAGE_SIZE,
  INITIAL_PAGE_SIZE,
} from './constants'

export interface VisibleRange {
  from: number
  to: number
}

export interface PageAndSize {
  page: number
  size: number
}

/**
 * The API paginates by page+size with size capped at 100. `FetchPlan` picks
 * a page+size that fetches a superset covering the requested range, plus
 * the offset/count to slice client-side back down to exactly [from..to].
 *
 * When the range is wider than the cap the plan fetches and returns the
 * largest contiguous window starting at `from` that fits in one API call.
 */
export interface FetchPlan extends PageAndSize {
  offset: number
  count: number
}

export function computeVisibleRange(
  page: number,
  pageSize: number,
  totalItems: number,
): VisibleRange {
  if (totalItems <= 0) return { from: 0, to: 0 }
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalItems)
  return { from, to }
}

/**
 * Given a visible range the user asked for (1-indexed, inclusive on both
 * ends), work out which page+size to fetch and how to slice the result.
 *
 * We fetch a stable page size (at least INITIAL_PAGE_SIZE, capped at
 * API_MAX_PAGE_SIZE) so navigating within a page doesn't keep changing the
 * React Query key — the cache can serve adjacent windows instantly instead
 * of firing a new request every time `from`/`to` shifts by a few rows.
 */
export function planFetch(from: number, to: number): FetchPlan {
  const safeFrom = Math.max(1, from)
  const safeTo = Math.max(safeFrom, to)
  const requestedCount = safeTo - safeFrom + 1

  const size = Math.min(
    API_MAX_PAGE_SIZE,
    Math.max(API_MIN_PAGE_SIZE, requestedCount, INITIAL_PAGE_SIZE),
  )
  const page = Math.max(1, Math.floor((safeFrom - 1) / size) + 1)
  const pageStart = (page - 1) * size + 1
  const pageEnd = page * size
  const offset = safeFrom - pageStart
  // A single API call can't reach past the end of the chosen page — cap
  // the slice count so the caller never claims rows that weren't returned.
  const count = Math.min(requestedCount, pageEnd - safeFrom + 1)
  return { page, size, offset, count }
}

// Kept for direct callers of the old range → page/size converter.
export function rangeToPagination(from: number, to: number): PageAndSize {
  const plan = planFetch(from, to)
  return { page: plan.page, size: plan.size }
}

/**
 * Which rows the bottom bar's current sub-page shows, in absolute dataset
 * coordinates. The DataDropdown window can be any size, but only one
 * sub-page (`size` rows, ≤ the API cap) is on screen at a time — so this is
 * the slice the page actually fetches.
 */
export function visibleSubRange(
  from: number,
  to: number,
  size: number,
  page: number,
): VisibleRange {
  const safePage = Math.max(1, page)
  const subFrom = from + (safePage - 1) * size
  const subTo = Math.min(to, from + safePage * size - 1)
  return { from: subFrom, to: subTo }
}

/**
 * The API can't return an arbitrary offset, only fixed `size`-row pages — so
 * we fetch with `size` = the "show per page" itself, not the max. A sub-range
 * of ≤ `size` rows then maps to exactly one such page when the window start is
 * aligned (the default `from=1`, and every page-aligned window), and to two
 * adjacent pages only when the window starts off the grid. `planWindowFetch`
 * names those page(s) and where to slice the concatenated result back down to
 * exactly [from..to].
 */
export interface WindowFetchPlan {
  page1: number
  page2: number
  twoPages: boolean
  size: number
  sliceStart: number
  sliceCount: number
}

export function planWindowFetch(
  from: number,
  to: number,
  size: number,
): WindowFetchPlan {
  const safeFrom = Math.max(1, from)
  const safeTo = Math.max(safeFrom, to)
  // The fetched page size is the show-per-page, bounded by the API cap — so we
  // pull only the rows the sub-page shows, not a full max-size page.
  const apiSize = Math.min(API_MAX_PAGE_SIZE, Math.max(1, size))
  const page1 = Math.floor((safeFrom - 1) / apiSize) + 1
  const page2 = Math.floor((safeTo - 1) / apiSize) + 1
  const blockStart = (page1 - 1) * apiSize + 1
  return {
    page1,
    page2,
    twoPages: page2 !== page1,
    size: apiSize,
    sliceStart: safeFrom - blockStart,
    sliceCount: safeTo - safeFrom + 1,
  }
}

/**
 * Concatenate the fetched page(s) and slice out the visible sub-range. Pass
 * `page2Items` only matters when `plan.twoPages` — it's ignored otherwise.
 */
export function sliceWindow<T>(
  page1Items: T[] | undefined,
  page2Items: T[] | undefined,
  plan: WindowFetchPlan,
): T[] {
  const combined = plan.twoPages
    ? [...(page1Items ?? []), ...(page2Items ?? [])]
    : (page1Items ?? [])
  return combined.slice(plan.sliceStart, plan.sliceStart + plan.sliceCount)
}

/**
 * Loading state for a windowed fetch: the first page must load, and — only
 * when the sub-range straddles into a second page — that one too.
 */
export function isWindowLoading(
  firstPending: boolean,
  secondPending: boolean,
  plan: WindowFetchPlan,
): boolean {
  return firstPending || (plan.twoPages && secondPending)
}
